import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  InputBase,
  Paper,
  styled,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import issue from "../../../assets/svg/issue.svg";
import { generateStoreColumns } from "../helper";
import DataTableComponent from "../../../components/common/DataTable/DataTable";
import { useNavigate } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db, storage } from "../../../firebaseConfig"; // Update with your Firebase configuration path
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/common/ConfirmationModal/ConfirmationModal";
import { getDownloadURL, ref } from "firebase/storage";
import Loader from "../../../assets/images/abc2.gif";
import StoreStepperContext from "../../../context/storeStepperContext";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "12px",
  backgroundColor: "#FAFAFA",
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  height: "40px",
  border: "1px solid #EEEEEE",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "230px",
  },
}));

function StoresList() {
  const navigate = useNavigate();
  const { setStepperActiveStep } = useContext(StoreStepperContext);
  const [storesData, setStoresData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [confirmatioModalState, setConfirmationModalState] = useState({
    showModal: false,
    storeId: "",
  });
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);

  const cancelHandler = () => {
    setConfirmationModalState({ showModal: false, storeId: "" });
  };

  useEffect(() => {
    const handler = async () => {
      setLoading2(true);
      try {
        const querySnapshot = collection(db, "stores");
        const q = query(querySnapshot, where("isDeleted", "==", false));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const arr = [];
          snapshot.forEach((doc) => {
            arr.push({ ...doc.data(), id: doc.id });
          });
          setStoresData(arr); // Update state with real-time data
        });
        setLoading2(false);
        return () => unsubscribe(); // Cleanup listener on component unmount
      } catch (err) {
        console.error(err, "Error in handler");
        setLoading2(false);
      }
    };

    handler();
  }, []); // Remove `loading` as a dependency

  const deleteHandler = async () => {
    setLoading(true); // Indicate loading state
    try {
      const storeDocRef = doc(db, "stores", confirmatioModalState.storeId); // Reference the document to delete
      await deleteDoc(storeDocRef); // Delete the document
      toast.success("Store Deleted Successfully");
    } catch (error) {
      console.error("Error deleting store:", error); // Log any errors
      toast.error("Failed to delete store. Please try again.");
    } finally {
      setLoading(false); // Reset loading state
      setConfirmationModalState({ showModal: false, storeId: "" }); // Close confirmation modal
    }
  };

  const menuOptions = useMemo(
    () => [
      {
        id: "edit",
        iconName: "mdi:square-edit-outline",
        actionName: "Edit",
        // redirectUrl:'/systems/route-partner/addNew',
        onClickHandle: (row) => {
          navigate("/shopping/store/edit", {
            state: { id: row?.id },
          });
        },
      },
      {
        id: "view-details",
        iconName: "ic:baseline-remove-red-eye",
        actionName: "View Details",
        onClickHandle: (row) => {
          navigate(`/shopping/order/view/${row?.id}`);
        },
      },
      {
        id: "delete",
        iconName: "material-symbols:delete-rounded",
        // actionName: "Delete",
        actionName: "Request For Delete",
        isDisable: false,
        onClickHandle: (row) => {
          setConfirmationModalState((prev) => ({
            showModal: true,
            storeId: row?.id,
          }));
        },
      },
    ],
    []
  );

  const storeColumns = generateStoreColumns(menuOptions);

  // Fetch data from Firestore
  useEffect(() => {
    const fetchStores = async () => {
      setLoading2(true);
      try {
        const storesCollectionRef = collection(db, "stores");
        const querySnapshot = await getDocs(storesCollectionRef);

        const stores = await Promise.all(
          querySnapshot.docs.map(async (doc) => {
            const data = doc.data();

            // Normalize storeImages as an array
            const imagePaths = Array.isArray(data.storeImages)
              ? data.storeImages
              : [data.storeImages].filter(Boolean);

            let storeImages = [];
            if (imagePaths.length > 0) {
              // Fetch updated URLs for all images
              storeImages = await Promise.all(
                imagePaths.map(async (imagePath) => {
                  try {
                    const imageRef = ref(storage, imagePath);
                    return await getDownloadURL(imageRef);
                  } catch (error) {
                    console.error(
                      "Error fetching image URL for store:",
                      data.storeName,
                      imagePath,
                      error
                    );
                    return imagePath; // Fallback to original path
                  }
                })
              );
            }

            return {
              id: doc.id,
              storeId: data.storeID,
              store_name: data.storeName,
              storeLocation: data.storeLocation,
              storeDescription: data.storeDescription,
              ...(data.storeDesignation && {
                storeDesignation: data.storeDesignation,
              }),
              storeImages, // Updated storeImages with URLs
              created_on: data.storeCreatedDate?.toDate()?.toLocaleDateString(),
              rating: data.rating || 0,
              total_products: data.products?.length || 0,
            };
          })
        );

        setStoresData(stores);
        setLoading2(false);
      } catch (error) {
        console.error("Error fetching stores:", error);
        setLoading2(false);
      }
    };

    fetchStores();
  }, [loading]);

  const filteredRows = useMemo(() => {
    return storesData?.filter((row) =>
      row?.store_name?.toLowerCase()?.includes(searchValue?.toLowerCase())
    );
  }, [storesData, searchValue]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ p: "24px 16px", paddingLeft: "0px" }}>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ "aria-label": "search" }}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </Search>
        </Box>
        {filteredRows.length > 0 && (
          <Box sx={{ display: "flex" }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#5E17EB",
                color: "#fff",
                marginLeft: "20px",
                borderRadius: "4px",
                fontWeight: 600,
                marginTop: "20px",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: "#5E17EB",
                },
              }}
              onClick={() => {
                navigate("/shopping/store/add-store");
                setStepperActiveStep(0);
              }}
            >
              Add Store
            </Button>
          </Box>
        )}
      </Box>

      {loading2 ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
          <img src={Loader} style={{ height: "80px" }} alt="loader" />
        </Box>
      ) : (
        <>
          {filteredRows?.length > 0 ? (
            <Box sx={{ display: "contents" }}>
              <Paper
                sx={{
                  width: "100%",
                  border: "1px solid #E5E5E8",
                  borderRadius: "4px",
                }}
              >
                <DataTableComponent
                  data={filteredRows || []}
                  columns={storeColumns}
                />
              </Paper>
            </Box>
          ) : (
            <Box sx={{ marginTop: "10%" }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img src={issue} alt="issue_not_found" />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "30px",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
                  No records found
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#5E17EB",
                    color: "#fff",
                    marginLeft: "20px",
                    borderRadius: "4px",
                    fontWeight: 600,
                    marginTop: "20px",
                    "&:hover": {
                      color: "#fff",
                      backgroundColor: "#5E17EB",
                    },
                  }}
                  onClick={() => navigate("/shopping/store/add-store")}
                >
                  Add Store
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
      <ConfirmationModal
        showModal={confirmatioModalState.showModal}
        submitHandler={() => deleteHandler()}
        submitText={"Delete"}
        cancelText={"Cancel"}
        cancelHandler={cancelHandler}
        headerText={"Are you sure?"}
        bodyText={"You want to delete this store"}
        loading={loading}
      />
    </>
  );
}

export default StoresList;
